import { type CompanyRole } from '@company/shared'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { get, put } from '@/lib/api'

/**
 * 团队信息
 */
export interface MyTeamInfo {
  id: string
  name: string
  role: string
  isLeader: boolean
}

/**
 * 项目信息
 */
export interface MyProjectInfo {
  id: string
  name: string
  code: string
  role: string
}

/**
 * 设备信息
 */
export interface MyDeviceInfo {
  id: string
  name: string
  type: string
  serialNumber: string
  assignedAt: string
}

/**
 * 个人资料
 */
export interface MyProfile {
  id: string
  employeeNo: string
  name: string
  email: string
  phone: string | null
  avatar: string | null
  role: CompanyRole
  department: { id: string; name: string } | null
  position: { id: string; name: string; level: number } | null
  status: string
  hireDate: string
  slackUserId: string | null
  slackLinkedAt: string | null
  team: MyTeamInfo | null
  projects: MyProjectInfo[]
  devices: MyDeviceInfo[]
}

/**
 * 更新个人资料输入
 */
export interface UpdateMyProfileInput {
  phone?: string | null
}

/**
 * Query keys
 */
export const myProfileKeys = {
  all: ['my-profile'] as const,
  detail: () => [...myProfileKeys.all, 'detail'] as const,
}

/**
 * 获取个人资料
 */
export function useMyProfile() {
  return useQuery({
    queryKey: myProfileKeys.detail(),
    queryFn: () => get<MyProfile>('/users/me/profile'),
  })
}

/**
 * 更新个人资料
 */
export function useUpdateMyProfile() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: UpdateMyProfileInput) => put('/users/me/profile', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: myProfileKeys.all })
    },
  })
}

/**
 * 更新头像
 */
export function useUpdateMyAvatar() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (avatarUrl: string) => put('/users/me/profile/avatar', { avatarUrl }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: myProfileKeys.all })
    },
  })
}
